import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QrCode, Plus, SignOut, Trash, FilePdf, CircleNotch, Gear, X, CheckCircle, FileArrowUp, ArrowSquareOut, PencilSimple, MagnifyingGlass, Scan, DownloadSimple, FileCsv, PaperPlaneTilt, FolderOpen, Stamp, Copy, Camera, BookOpen } from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { PlatePreview } from "../components/PlatePreview";
import { ScansModal } from "../components/ScansModal";
import { ShareModal } from "../components/ShareModal";
import { DossierModal } from "../components/DossierModal";
import { DossierSearch } from "../components/DossierSearch";
import { ContactRequests } from "../components/ContactRequests";
import { ManualQrEditor } from "../components/ManualQrEditor";
import { ScanStats } from "../components/ScanStats";
import { AdminPanel } from "../components/AdminPanel";
import { STRIPE_LINKS, withEmail } from "../lib/stripeLinks";
import api from "../lib/api";

const empty = { name: "", model: "", serial_number: "", manufacturer: "", year: "", plate_bg: "", description: "", video_url: "" };
const emptyDocs = { manual: null, declaration: null };

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [docs, setDocs] = useState(emptyDocs);
  const [saving, setSaving] = useState(false);
  const [genId, setGenId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [dossierFilter, setDossierFilter] = useState("all");
  const [scansFor, setScansFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [dossierFor, setDossierFor] = useState(null);
  const [qrEditFor, setQrEditFor] = useState(null);
  const [plan, setPlan] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const fmtGb = (b) => ((b || 0) / 1073741824).toFixed(2).replace(/\.?0+$/, "") || "0";

  const fetchLogo = async () => {
    try {
      const res = await api.get("/me/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(res.data));
    } catch (e) {
      setLogoUrl(null);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/me/logo", fd);
      toast.success(lang === "it" ? "Logo aziendale caricato" : "Company logo uploaded");
      fetchLogo();
      load();
    } catch (e) {
      toast.error(lang === "it" ? "Errore caricamento logo" : "Logo upload error");
    }
  };

  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (d === "subscription_required") return t.dash.errSubscription;
    if (d === "archive_required") return t.dash.errArchive;
    if (d === "archive_limit_reached") return t.dash.errArchiveLimit;
    if (d === "archive_storage_limit_reached") return t.dash.errStorageLimit;
    return fallback;
  };

  const filtered = machines.filter((m) =>
    `${m.name} ${m.serial_number} ${m.manufacturer} ${m.model}`.toLowerCase().includes(search.toLowerCase()) &&
    (dossierFilter === "all" || (dossierFilter === "with" ? (m.dossier_count || 0) > 0 : (m.dossier_count || 0) === 0))
  );

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(empty);
    setDocs(emptyDocs);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(empty);
    setDocs(emptyDocs);
    setShowForm(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, model: m.model, serial_number: m.serial_number, manufacturer: m.manufacturer, year: m.year || "", plate_bg: m.plate_bg || "", description: m.description || "", video_url: m.video_url || "" });
    setDocs(emptyDocs);
    setShowForm(true);
  };

  const load = async () => {
    try {
      const [{ data }, planRes] = await Promise.all([api.get("/machines"), api.get("/me/plan")]);
      setMachines(data);
      setPlan(planRes.data);
      if (planRes.data.has_logo) fetchLogo();
    } catch (e) {
      toast.error(lang === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const refreshPlan = () => {
      if (document.visibilityState === "visible") {
        api.get("/me/plan").then(({ data }) => setPlan(data)).catch(() => {});
      }
    };
    window.addEventListener("focus", refreshPlan);
    document.addEventListener("visibilitychange", refreshPlan);
    return () => {
      window.removeEventListener("focus", refreshPlan);
      document.removeEventListener("visibilitychange", refreshPlan);
    };
  }, []);

  const logout = async () => {
    sessionStorage.removeItem("cqr_impersonating");
    navigate("/", { replace: true });
    await supabase.auth.signOut();
  };

  const impersonating = sessionStorage.getItem("cqr_impersonating");
  const exitImpersonation = async () => {
    sessionStorage.removeItem("cqr_impersonating");
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const duplicateMachine = async (m) => {
    try {
      const { data: dup } = await api.post(`/machines/${m.id}/duplicate?lang=${lang}`);
      toast.success(t.dash.duplicated);
      await load();
      openEdit(dup);
    } catch (e) {
      toast.error(lang === "it" ? "Errore nella duplicazione" : "Duplication error");
    }
  };

  const photoInputRef = useRef(null);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [photoVer, setPhotoVer] = useState(Date.now());

  const pickPhoto = (m) => {
    setPhotoTarget(m.id);
    photoInputRef.current?.click();
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !photoTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/machines/${photoTarget}/photo`, fd);
      toast.success(t.dash.photoSaved);
      setPhotoVer(Date.now());
      load();
    } catch (err) {
      toast.error(t.dash.photoError);
    }
  };

  const removePhoto = async (m) => {
    try {
      await api.delete(`/machines/${m.id}/photo`);
      toast.success(t.dash.photoRemoved);
      setPhotoVer(Date.now());
      load();
    } catch (err) {
      toast.error(t.dash.photoError);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let machineId;
      if (editing) {
        await api.put(`/machines/${editing.id}`, form);
        machineId = editing.id;
      } else {
        const { data: created } = await api.post("/machines", form);
        machineId = created.id;
      }
      for (const type of ["manual", "declaration"]) {
        if (docs[type]) {
          const fd = new FormData();
          fd.append("file", docs[type]);
          await api.post(`/machines/${machineId}/documents/${type}`, fd);
        }
      }
      const wasEditing = !!editing;
      closeForm();
      toast.success(wasEditing ? t.dash.updatedToast : (lang === "it" ? "Macchinario aggiunto" : "Machine added"));
      load();
    } catch (e) {
      toast.error(errMsg(e, lang === "it" ? "Errore nel salvataggio" : "Save error"));
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "it" ? "Eliminare questo macchinario?" : "Delete this machine?")) return;
    try {
      await api.delete(`/machines/${id}`);
      setMachines((m) => m.filter((x) => x.id !== id));
      toast.success(lang === "it" ? "Eliminato" : "Deleted");
    } catch (e) {
      toast.error(lang === "it" ? "Errore" : "Error");
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/me/portal");
      window.location.href = data.url;
    } catch (e) {
      toast.error(lang === "it" ? "Portale clienti non disponibile al momento" : "Customer portal unavailable right now");
    }
  };

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadGuide = async () => {
    try {
      const res = await api.get(`/guide?lang=${lang}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "guida-rapida-certificoqr.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t.dash.guideError); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/machines/export.csv?t=${Date.now()}`, { responseType: "blob" });
      downloadBlob(res.data, "certifico-qr-macchinari.csv");
    } catch (e) {
      toast.error(lang === "it" ? "Errore export" : "Export error");
    }
  };

  const downloadPlate = async (m, plang) => {
    try {
      const useLangCode = plang || lang;
      const res = await api.get(`/machines/${m.id}/plate.pdf?lang=${useLangCode}&t=${Date.now()}`, { responseType: "blob" });
      downloadBlob(res.data, `targa-${useLangCode}-${m.serial_number}.pdf`);
    } catch (e) {
      toast.error(errMsg(e, lang === "it" ? "Errore download targa" : "Plate download error"));
    }
  };

  const downloadAllPlates = async () => {
    try {
      const res = await api.get(`/machines/plates.zip?lang=${lang}&t=${Date.now()}`, { responseType: "blob" });
      downloadBlob(res.data, "certifico-qr-targhe.zip");
    } catch (e) {
      toast.error(errMsg(e, lang === "it" ? "Errore download targhe" : "Plates download error"));
    }
  };

  const generate = async (id) => {
    setGenId(id);
    try {
      const { data } = await api.post(`/machines/${id}/generate-plate`);
      if (data.ok) toast.success(lang === "it" ? "Generazione targa avviata" : "Plate generation started");
      else toast.warning(lang === "it" ? `Generazione non riuscita: ${data.status}` : `Generation failed: ${data.status}`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore generazione targa"));
    } finally {
      setGenId(null);
    }
  };

  const docLink = (m, type) => {
    if (m[`${type}_file_path`]) return `${process.env.REACT_APP_BACKEND_URL}/api/public/machines/${m.id}/documents/${type}`;
    return m[`${type}_url`] || "";
  };

  const fileField = (type, label) => (
    <div>
      <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
        {label} <span className="text-slate-400 normal-case">· {t.dash.optional}</span>
      </label>
      <label className={`flex items-center gap-2.5 rounded-md border border-dashed px-3.5 py-3 text-sm cursor-pointer transition ${docs[type] ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500 hover:border-emerald-400"}`}>
        <FileArrowUp size={18} className="shrink-0" />
        <span className="truncate">{docs[type] ? docs[type].name : t.dash.uploadHint}</span>
        <input type="file" accept="application/pdf" className="hidden" data-testid={`machine-${type}-file-input`}
          onChange={(e) => setDocs({ ...docs, [type]: e.target.files[0] || null })} />
      </label>
    </div>
  );

  const field = (key, type = "text", required = true) => (
    <div>
      <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{t.dash[key === "serial_number" ? "serial" : key === "manual_url" ? "manual" : key === "declaration_url" ? "declaration" : key]}</label>
      <input type={type} required={required} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
        data-testid={`machine-${key}-input`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard-page">
      <Toaster position="top-right" richColors />
      {impersonating && user?.email === impersonating && (
        <div className="bg-amber-400 text-[#1F1300] px-6 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-semibold" data-testid="impersonation-banner">
          <span>{t.dash.impersonating.replace("{email}", impersonating)}</span>
          <button onClick={exitImpersonation} className="underline underline-offset-2 hover:opacity-70 transition-opacity" data-testid="impersonation-exit">{t.dash.impersonateExit}</button>
        </div>
      )}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center bg-[#0A0F1A] rounded-[4px]"><QrCode size={18} weight="bold" className="text-white" /></div>
            <span className="font-display font-bold text-[17px] tracking-tight">Certifico<span className="text-emerald-500">QR</span></span>
            <span className="ml-3 hidden sm:inline-block font-mono-tech text-[11px] uppercase tracking-wider text-slate-400 border-l border-slate-200 pl-3">{t.dash.title}</span>
          </div>
          <div className="flex items-center gap-3">
            {plan?.is_admin && (
              <button onClick={() => setShowAdmin(true)} className="inline-flex items-center gap-1.5 font-mono-tech text-xs font-semibold border border-indigo-300 text-indigo-700 bg-indigo-50 rounded-[4px] px-2.5 py-1 hover:bg-indigo-100 transition-colors" data-testid="dashboard-admin-btn">
                {t.dash.adminBtn}
              </button>
            )}
            <button onClick={toggle} className="font-mono-tech text-xs font-semibold border border-slate-300 rounded-[4px] px-2 py-1" data-testid="dashboard-lang-toggle">{lang.toUpperCase()}</button>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-red-600 transition-colors" data-testid="dashboard-logout">
              <SignOut size={18} /> <span className="hidden sm:inline">{t.dash.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-emerald-600">{t.dash.welcome}, {user?.email}</p>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0F172A]">{t.dash.title}</h1>
            {plan && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="dashboard-plan-badges">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${plan.subscription_active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="plan-subscription-badge">
                  <span className={`h-1.5 w-1.5 rounded-full ${plan.subscription_active ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {plan.subscription_active ? `${t.dash.subActive}${plan.subscription ? ` · ${plan.subscription === "annual" ? (lang === "it" ? "Annuale" : "Annual") : (lang === "it" ? "Mensile" : "Monthly")}` : ""}` : t.dash.subInactive}
                </span>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${plan.archive_package ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-700"}`} data-testid="plan-archive-badge">
                  {plan.archive_package
                    ? `${t.dash.archiveLabel} ${plan.archive_package === "pro" ? "Pro" : "Base"} · ${plan.archive_used}/${plan.archive_limit} ${t.dash.manualsLabel} · ${fmtGb(plan.archive_storage_used)}/${fmtGb(plan.archive_storage_limit)} GB`
                    : t.dash.archiveNone}
                </span>
                {plan.fascicolo_package && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-700" data-testid="plan-fascicolo-badge">
                    <FolderOpen size={12} /> {t.dash.fascicoloBadge}
                  </span>
                )}
                {plan.has_customer && (
                  <button onClick={openPortal} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid="plan-portal-link">
                    <Gear size={12} /> {t.dash.managePortal}
                  </button>
                )}
              </div>
            )}
            <p className="mt-1 text-sm text-slate-500">{machines.length} {t.dash.count}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 self-start">
            <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors cursor-pointer" data-testid="dashboard-upload-logo">
              {logoUrl ? <img src={logoUrl} alt="logo" className="h-5 w-auto max-w-[60px] object-contain" data-testid="dashboard-logo-thumb" /> : <FileArrowUp size={18} />}
              {t.dash.companyLogo}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="dashboard-logo-input" />
            </label>
            {(plan?.subscription_active || plan?.archive_package || plan?.fascicolo_package) && (
              <button onClick={downloadGuide} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid="dashboard-guide-btn">
                <BookOpen size={18} /> {t.dash.guideBtn}
              </button>
            )}
            <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid="dashboard-export-csv">
              <FileCsv size={18} /> {t.dash.exportCsv}
            </button>
            <button onClick={downloadAllPlates} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid="dashboard-download-plates-zip">
              <DownloadSimple size={18} /> {t.dash.downloadAllPlates}
            </button>
            <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors" data-testid="dashboard-add">
              <Plus size={18} weight="bold" /> {t.dash.add}
            </button>
          </div>
        </div>

        {plan && !plan.subscription_active && (
          <div className="mb-8 rounded-md border border-amber-300 bg-amber-50 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dashboard-upgrade-banner">
            <div className="flex-1">
              <p className="font-semibold text-amber-900">{t.dash.upgradeTitle}</p>
              <p className="mt-0.5 text-sm text-amber-800">{t.dash.upgradeSub}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={withEmail(STRIPE_LINKS.monthly, user?.email)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors"
                data-testid="upgrade-monthly-btn">{t.dash.upgradeCta} · €19 {t.dash.upgradeMonthly}</a>
              <a href={withEmail(STRIPE_LINKS.annual, user?.email)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 px-5 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
                data-testid="upgrade-annual-btn">€190 {t.dash.upgradeAnnual}</a>
            </div>
          </div>
        )}

        {plan?.archive_package && plan.archive_limit > 0 && plan.archive_used >= plan.archive_limit * 0.8 && (
          <div className={`mb-8 rounded-md border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${plan.archive_used >= plan.archive_limit ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}
            data-testid="archive-limit-banner">
            <div className="flex-1">
              <p className={`font-semibold ${plan.archive_used >= plan.archive_limit ? "text-red-900" : "text-amber-900"}`}>
                {plan.archive_used >= plan.archive_limit
                  ? t.dash.limitReachedTitle
                  : t.dash.limitNearTitle.replace("{used}", plan.archive_used).replace("{limit}", plan.archive_limit)}
              </p>
              <p className={`mt-0.5 text-sm ${plan.archive_used >= plan.archive_limit ? "text-red-800" : "text-amber-800"}`}>
                {plan.archive_used >= plan.archive_limit ? t.dash.limitReachedSub : t.dash.limitNearSub}
              </p>
            </div>
            {plan.archive_package !== "pro" && (
              <a href={withEmail(STRIPE_LINKS.archivePro, user?.email)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors shrink-0"
                data-testid="archive-limit-upgrade-btn">{t.dash.limitCta}</a>
            )}
          </div>
        )}

        {plan?.archive_package && plan.archive_storage_limit > 0 && plan.archive_storage_used >= plan.archive_storage_limit * 0.8 && (
          <div className={`mb-8 rounded-md border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${plan.archive_storage_used >= plan.archive_storage_limit ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}
            data-testid="storage-limit-banner">
            <div className="flex-1">
              <p className={`font-semibold ${plan.archive_storage_used >= plan.archive_storage_limit ? "text-red-900" : "text-amber-900"}`}>
                {(plan.archive_storage_used >= plan.archive_storage_limit ? t.dash.storageReachedTitle : t.dash.storageNearTitle)
                  .replace("{used}", fmtGb(plan.archive_storage_used)).replace("{limit}", fmtGb(plan.archive_storage_limit))}
              </p>
              <p className={`mt-0.5 text-sm ${plan.archive_storage_used >= plan.archive_storage_limit ? "text-red-800" : "text-amber-800"}`}>
                {plan.archive_storage_used >= plan.archive_storage_limit ? t.dash.storageReachedSub : t.dash.storageNearSub}
              </p>
            </div>
            {!plan.fascicolo_package && (
              <a href={withEmail(STRIPE_LINKS.fascicoloPro, user?.email)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors shrink-0"
                data-testid="storage-limit-upgrade-btn">{t.dash.storageCta}</a>
            )}
          </div>
        )}

        {machines.length > 0 && <ScanStats />}

        <ContactRequests />

        {machines.length > 0 && plan?.fascicolo_package && <DossierSearch />}

        <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPhotoFile} className="hidden" data-testid="machine-photo-input" />
        {machines.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1 min-w-[240px]">
              <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.dash.search}
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-3 text-sm text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
                data-testid="dashboard-search-input" />
            </div>
            {plan?.fascicolo_package && (
              <div className="flex items-center gap-1.5" data-testid="dossier-filter">
                {[["all", t.dash.filterAll], ["with", t.dash.filterWith], ["without", t.dash.filterWithout]].map(([k, label]) => (
                  <button key={k} onClick={() => setDossierFilter(k)}
                    className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${dossierFilter === k ? "bg-emerald-500 text-[#04170F]" : "border border-slate-300 bg-white text-slate-600 hover:border-emerald-500 hover:text-emerald-700"}`}
                    data-testid={`dossier-filter-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><CircleNotch size={32} className="text-emerald-500 animate-spin" /></div>
        ) : machines.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-md py-24 text-center bg-white" data-testid="dashboard-empty">
            <Gear size={40} className="mx-auto text-slate-300" />
            <p className="mt-4 max-w-sm mx-auto text-sm text-slate-500">{t.dash.empty}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-md py-16 text-center bg-white" data-testid="dashboard-no-results">
            <MagnifyingGlass size={32} className="mx-auto text-slate-300" />
            <p className="mt-4 text-sm text-slate-500">{t.dash.noResults}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((m, i) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white border border-slate-200 rounded-md p-6 hover:border-emerald-400 transition-colors overflow-hidden" data-testid={`machine-card-${i}`}>
                {m.photo_path && (
                  <div className="relative -mx-6 -mt-6 mb-5 h-36 group" data-testid={`machine-photo-${i}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/machines/${m.id}/photo?v=${photoVer}`} alt={m.name} className="h-full w-full object-cover" />
                    <button onClick={() => removePhoto(m)} title={t.dash.photoRemove}
                      className="absolute top-2 right-2 rounded-full bg-black/50 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                      data-testid={`machine-photo-remove-${i}`}><X size={13} weight="bold" /></button>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-[4px] bg-[#0A0F1A]"><QrCode size={22} weight="bold" className="text-emerald-500" /></div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => pickPhoto(m)} className="text-slate-300 hover:text-emerald-600 transition-colors p-1" title={m.photo_path ? t.dash.photoChange : t.dash.photoUpload} data-testid={`machine-photo-btn-${i}`}><Camera size={18} /></button>
                    <button onClick={() => openEdit(m)} className="text-slate-300 hover:text-emerald-600 transition-colors p-1" title={t.dash.edit} data-testid={`machine-edit-${i}`}><PencilSimple size={18} /></button>
                    <button onClick={() => remove(m.id)} className="text-slate-300 hover:text-red-500 transition-colors p-1" data-testid={`machine-delete-${i}`}><Trash size={18} /></button>
                  </div>
                </div>
                <h3 className="mt-4 font-display font-bold text-lg text-[#0F172A]">{m.name}</h3>
                <p className="text-sm text-slate-500">{m.manufacturer} · {m.model}</p>
                <div className="mt-3 font-mono-tech text-[11px] text-slate-400">S/N: {m.serial_number}{m.year ? ` · ${m.year}` : ""}</div>

                {(docLink(m, "manual") || docLink(m, "declaration")) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {docLink(m, "manual") && (
                      <a href={docLink(m, "manual")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors" data-testid={`machine-manual-link-${i}`}>
                        <FilePdf size={12} /> {t.dash.manualShort}
                      </a>
                    )}
                    {docLink(m, "declaration") && (
                      <a href={docLink(m, "declaration")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors" data-testid={`machine-declaration-link-${i}`}>
                        <FilePdf size={12} /> {t.dash.declShort}
                      </a>
                    )}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {m.plate_generated && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600"><CheckCircle size={14} weight="fill" /> {t.dash.generated}</span>
                  )}
                  <button onClick={() => setScansFor(m)} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors" data-testid={`machine-scans-${i}`}>
                    <Scan size={12} /> {m.scan_count || 0} {(m.scan_count || 0) === 1 ? t.scans.one : t.scans.badge}
                  </button>
                  {plan?.fascicolo_package && (m.dossier_count || 0) > 0 && (
                    <button onClick={() => setDossierFor(m)} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors" data-testid={`machine-doccount-${i}`}>
                      <FolderOpen size={12} /> {m.dossier_count} {m.dossier_count === 1 ? t.dossier.docOne : t.dossier.docMany}
                    </button>
                  )}
                </div>

                <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
                  <button onClick={() => setPreview(m)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0A0F1A] py-2.5 text-xs font-semibold text-white hover:bg-[#0F172A] transition-colors" data-testid={`machine-preview-${i}`}>
                    <QrCode size={14} weight="bold" /> {t.plate.preview}
                  </button>
                  <button onClick={() => downloadPlate(m)} title={t.dash.downloadPlate} className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-download-plate-${i}`}><DownloadSimple size={16} /></button>
                  <button onClick={() => setDossierFor(m)} title={t.dossier.title} className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-dossier-${i}`}><FolderOpen size={16} /></button>
                  {(m.manual_file_path || m.declaration_file_path) && plan?.archive_package && (
                    <button onClick={() => setQrEditFor(m)} title={t.dash.applyQr} className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-qredit-${i}`}><Stamp size={16} /></button>
                  )}
                  <button onClick={() => duplicateMachine(m)} title={t.dash.duplicate} className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-duplicate-${i}`}><Copy size={16} /></button>
                  <button onClick={() => setShareFor(m)} title={t.share.title} className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-share-${i}`}><PaperPlaneTilt size={16} /></button>
                  <a href={`/m/${m.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" title="Pagina pubblica QR" data-testid={`machine-public-${i}`}><ArrowSquareOut size={16} /></a>
                  {m.plate_pdf_url && (
                    <a href={m.plate_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors" data-testid={`machine-pdf-${i}`}><FilePdf size={16} /></a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/50 backdrop-blur-sm" onClick={closeForm}>
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-md border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="machine-form">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
                <h3 className="font-display font-bold text-xl text-[#0F172A]">{editing ? t.dash.editTitle : t.dash.add}</h3>
                <button onClick={closeForm} className="text-slate-400 hover:text-[#0F172A]" data-testid="machine-form-close"><X size={20} /></button>
              </div>
              <form onSubmit={save} className="px-6 py-6 space-y-4">
                {field("name")}
                <div className="grid grid-cols-2 gap-4">{field("model")}{field("serial_number")}</div>
                <div className="grid grid-cols-2 gap-4">{field("manufacturer")}{field("year", "text", false)}</div>
                <div>
                  <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{t.dash.descLabel}</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={600} rows={3}
                    placeholder={t.dash.descPlaceholder}
                    className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500 resize-none"
                    data-testid="machine-description-input" />
                </div>
                <div>
                  <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{t.dash.videoLabel}</label>
                  <input type="url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500"
                    data-testid="machine-video-input" />
                </div>
                {fileField("manual", t.dash.manualFile)}
                {fileField("declaration", t.dash.declFile)}
                <div>
                  <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{t.dash.plateColor}</label>
                  <div className="flex flex-wrap items-center gap-2" data-testid="plate-color-picker">
                    <button type="button" onClick={() => setForm({ ...form, plate_bg: "" })}
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${!form.plate_bg || form.plate_bg === "#0e1425" ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-600"}`}
                      data-testid="plate-color-dark">
                      <span className="h-4 w-4 rounded-full border border-slate-300" style={{ backgroundColor: "#0E1425" }} /> {t.dash.plateDark}
                    </button>
                    <button type="button" onClick={() => setForm({ ...form, plate_bg: "#FFFFFF" })}
                      className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${form.plate_bg?.toUpperCase() === "#FFFFFF" ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-600"}`}
                      data-testid="plate-color-light">
                      <span className="h-4 w-4 rounded-full border border-slate-300 bg-white" /> {t.dash.plateLight}
                    </button>
                    <label className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold cursor-pointer transition-colors ${form.plate_bg && form.plate_bg.toUpperCase() !== "#FFFFFF" ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-600"}`}>
                      <input type="color" value={form.plate_bg || "#0E1425"} onChange={(e) => setForm({ ...form, plate_bg: e.target.value })}
                        className="h-4 w-4 rounded-full border-0 p-0 bg-transparent cursor-pointer" data-testid="plate-color-custom-input" />
                      {t.dash.plateCustom}
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={closeForm} className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="machine-form-cancel">{t.dash.cancel}</button>
                  <button type="submit" disabled={saving} className="flex-1 rounded-full bg-emerald-500 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="machine-form-save">{saving ? t.dash.uploading : editing ? t.dash.updateSave : t.dash.save}</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {preview && (
          <PlatePreview machine={preview} logoUrl={logoUrl} onClose={() => setPreview(null)} onSend={() => generate(preview.id)} sending={genId === preview.id} onDownload={(l) => downloadPlate(preview, l)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {scansFor && <ScansModal machine={scansFor} onClose={() => setScansFor(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {shareFor && <ShareModal machine={shareFor} onClose={() => setShareFor(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {dossierFor && <DossierModal machine={dossierFor} plan={plan} onClose={() => setDossierFor(null)} />}
      </AnimatePresence>

      <AnimatePresence>
        {qrEditFor && <ManualQrEditor machine={qrEditFor} onClose={() => setQrEditFor(null)} onSaved={load} />}
      </AnimatePresence>

      <AnimatePresence>
        {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
      </AnimatePresence>
    </div>
  );
}
